import React from 'react';
import { HealthAssessment } from '@/types';
import { ClipboardList } from 'lucide-react';

interface HealthAssessmentViewProps {
  data: HealthAssessment;
}

const Row = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
  <div className="flex justify-between items-start py-1.5 border-b border-slate-50 last:border-0">
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 shrink-0 mr-4">
      {label}
    </span>
    <span className="text-xs font-semibold text-slate-700 text-right">{value}</span>
  </div>
);

const OutcomeBadge = ({ value }: { value: 'acceptable' | 'refer' | undefined }) => {
  if (!value) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <span
      className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${
        value === 'acceptable' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
      }`}
    >
      {value}
    </span>
  );
};

const SectionHeader = ({ title }: { title: string }) => (
  <div className="bg-slate-100 px-3 py-1.5 rounded-lg mb-2 mt-4 first:mt-0">
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{title}</p>
  </div>
);

export const HealthAssessmentView: React.FC<HealthAssessmentViewProps> = ({ data }) => {
  // A screening assessment (D&A-only visit) records no medical exam — hide the
  // medical sections instead of rendering rows of empty values.
  const isScreening = data.assessmentType === 'screening';
  return (
    <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
      <div className="bg-slate-50 px-4 py-3 border-b flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-teal-600" />
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {isScreening ? 'D&A Screening Record' : 'Health Assessment'}
        </span>
        {isScreening && (
          <span className="ml-auto px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-purple-50 text-purple-700 border border-purple-100">
            D&A Only
          </span>
        )}
      </div>
      <div className="p-4 text-sm">
        <SectionHeader title="Patient Understanding" />
        <Row
          label="Reason for attending"
          value={data.understands.reasonForAttending ? 'Yes' : 'No'}
        />
        <Row label="Understands who you are" value={data.understands.whoYouAre ? 'Yes' : 'No'} />
        <Row label="Data usage understood" value={data.understands.dataUsage ? 'Yes' : 'No'} />

        {!isScreening && (
          <>
            <SectionHeader title="Biometrics" />
            <Row label="Height" value={data.height} />
            <Row label="Weight" value={data.weight} />
            <Row label="BMI" value={data.bmi} />
            <Row label="Blood Pressure 1" value={data.bloodPressure1} />
            <Row label="Blood Pressure 2" value={data.bloodPressure2} />
            <Row label="Blood Pressure 3" value={data.bloodPressure3} />
            <Row label="Pulse" value={data.pulse} />

            <SectionHeader title="Urinalysis" />
            <Row label="NAD" value={data.urinalysis.nad ? 'Yes' : 'No'} />
            <Row label="pH" value={data.urinalysis.ph} />
            <Row label="Ketones" value={data.urinalysis.ketones ? 'Yes' : 'No'} />
            <Row label="Protein" value={data.urinalysis.protein ? 'Yes' : 'No'} />
            <Row label="Glucose" value={data.urinalysis.glucose ? 'Yes' : 'No'} />
            <Row label="Blood" value={data.urinalysis.blood ? 'Yes' : 'No'} />
          </>
        )}

        <SectionHeader title="Drugs & Alcohol" />
        <Row label="Uses Drugs" value={data.usesDrugs} />
        <Row label="Drinks Alcohol" value={data.drinksAlcohol} />
        {data.donorIdChecked !== undefined && (
          <>
            <Row label="Donor ID Checked" value={data.donorIdChecked ? 'Yes' : 'No'} />
            {data.donorIdMethod && <Row label="Donor ID Method" value={data.donorIdMethod} />}
            {data.donorIdVerifiedBy && (
              <Row label="ID Verified By" value={data.donorIdVerifiedBy} />
            )}
            <Row label="Drug Tested" value={data.drugTested ? 'Yes' : 'No'} />
            <Row label="Drug Outcome" value={<OutcomeBadge value={data.drugOutcome} />} />
            <Row label="Alcohol Tested" value={data.alcoholTested ? 'Yes' : 'No'} />
            <Row label="Alcohol Outcome" value={<OutcomeBadge value={data.alcoholOutcome} />} />
          </>
        )}

        {!isScreening && (
          <>
            <SectionHeader title="Hearing" />
            <Row label="Otoscopy L" value={data.hearing.otoscopyL} />
            <Row label="Otoscopy R" value={data.hearing.otoscopyR} />
            <Row label="Category" value={data.hearing.category} />
            <Row label="Outcome" value={<OutcomeBadge value={data.hearing.outcome} />} />

            <SectionHeader title="Visual Acuity" />
            <Row
              label="Peripheral meets SOM"
              value={data.visualAcuity.peripheralMeetsSom ? 'Yes' : 'No'}
            />
            <Row label="Snellens Without L" value={data.visualAcuity.snellensWithoutL} />
            <Row label="Snellens Without R" value={data.visualAcuity.snellensWithoutR} />
            <Row label="Snellens Corrective L" value={data.visualAcuity.snellensCorrectiveL} />
            <Row label="Snellens Corrective R" value={data.visualAcuity.snellensCorrectiveR} />
            <Row
              label="Visual Acuity Outcome"
              value={<OutcomeBadge value={data.visualAcuity.outcome} />}
            />
            <Row label="Colour Perception" value={<OutcomeBadge value={data.colourPerception} />} />

            <SectionHeader title="Lung Function & Mobility" />
            <Row label="Lung Function" value={<OutcomeBadge value={data.lungFunction} />} />
            <Row label="MSD's / Mobility" value={<OutcomeBadge value={data.msdMobility} />} />

            <SectionHeader title="Mental Health" />
            {data.mentalHealth.comment && <Row label="Comment" value={data.mentalHealth.comment} />}
            <Row label="Outcome" value={<OutcomeBadge value={data.mentalHealth.outcome} />} />

            <SectionHeader title="General Health" />
            <Row label="Outcome" value={<OutcomeBadge value={data.generalHealth.outcome} />} />
            {data.generalHealth.details && (
              <Row label="Details" value={data.generalHealth.details} />
            )}
          </>
        )}

        <SectionHeader title="Medication" />
        <Row label="Medication" value={data.medication.hasMedication ? 'Yes' : 'No'} />
        {data.medication.details && <Row label="Details" value={data.medication.details} />}

        <SectionHeader title="Fitness" />
        <Row
          label="Fit Status"
          value={
            <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-teal-50 text-teal-700">
              {data.fitStatus.replace(/_/g, ' ')}
            </span>
          }
        />
        {data.fitStatus === 'fit_restrictions' && (data.restrictions?.length ?? 0) > 0 && (
          <Row
            label="Restrictions"
            value={
              <ul className="list-disc list-inside text-left space-y-0.5">
                {data.restrictions?.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            }
          />
        )}
        {data.recall && <Row label="Recall" value={data.recall} />}

        {data.clinicalNotes && (
          <>
            <SectionHeader title="Clinical Notes" />
            <p className="text-xs text-slate-700 font-medium leading-relaxed">
              {data.clinicalNotes}
            </p>
            {data.continuedOverleaf && (
              <p className="text-[10px] text-slate-400 font-semibold mt-1">Continued overleaf</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
